'use client';

import { useEffect, useState } from 'react';
import { useLandingTranslations } from './LandingLocaleProvider';
import { Reveal } from './Reveal';

/**
 * 데스크탑 설치 파일 다운로드 베이스.
 * 사용자-facing 다운로드/자동 업데이트 채널은 R2 custom domain이 canonical이며,
 * private GitHub release asset URL은 사용하지 않는다.
 *
 * 파일명은 버전 비의존 안정 alias로, desktop-release.yml의 R2 업로드 step이 릴리스마다 갱신한다.
 */
const DESKTOP_DOWNLOAD_BASE = 'https://dl.agentteams.run/desktop';

const DOWNLOADS = {
  // Apple Silicon 전용 — Intel(x64) Mac 빌드는 더 이상 제공하지 않는다.
  macArm64: `${DESKTOP_DOWNLOAD_BASE}/AgentTeams-mac-arm64.dmg`,
  win64: `${DESKTOP_DOWNLOAD_BASE}/AgentTeams-win-x64.exe`,
} as const;

type VariantId = 'macArm64' | 'win64';
type DetectedOS = 'mac' | 'windows' | null;
type DetectedArch = 'arm64' | 'x64' | null;

const VARIANT_ORDER: VariantId[] = ['macArm64', 'win64'];

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
      <path d="M16.36 12.78c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.75 2.28-1.6 2.78-.41 6.89 1.15 9.15.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.65zM14.13 5.81c.64-.78 1.07-1.85.95-2.93-.92.04-2.04.61-2.7 1.38-.59.68-1.11 1.78-.97 2.83 1.03.08 2.08-.52 2.72-1.28z" />
    </svg>
  );
}

function WindowsIcon() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
      <path d="M3 5.6 10.4 4.6v6.9H3V5.6zm0 12.8 7.4 1v-6.8H3v5.8zM11.3 4.5 21 3.1v8.4h-9.7V4.5zm0 15 9.7 1.4v-8.4h-9.7v7z" />
    </svg>
  );
}

function DesktopIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

/**
 * 방문자의 OS/아키텍처를 클라이언트에서 추정한다.
 * 정적 빌드(output: export)라 서버 분기가 불가하므로, SSR/첫 렌더는 null(=전체 노출)로
 * 시작해 hydration 불일치를 피하고, 마운트 후 effect에서 감지값을 채운다.
 */
function useDetectedPlatform(): { os: DetectedOS; arch: DetectedArch } {
  const [os, setOs] = useState<DetectedOS>(null);
  const [arch, setArch] = useState<DetectedArch>(null);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    const uaData = (
      navigator as Navigator & {
        userAgentData?: {
          platform?: string;
          getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
        };
      }
    ).userAgentData;
    const ua = navigator.userAgent ?? '';
    const platform = uaData?.platform?.toLowerCase() ?? '';

    let detectedOs: DetectedOS = null;
    if (platform.includes('mac') || /macintosh|mac os x/i.test(ua)) detectedOs = 'mac';
    else if (platform.includes('win') || /windows/i.test(ua)) detectedOs = 'windows';
    // iOS/Android/Linux 등은 null로 두어 전체 버튼 폴백을 노출한다.

    setOs(detectedOs);

    if (detectedOs === 'mac') {
      // Apple Silicon을 기본값으로 두고, 가능하면 high-entropy로 정정한다.
      // (Safari 등 userAgentData 미지원 환경에서는 최신 Mac 다수인 arm64가 안전한 기본값)
      setArch('arm64');
      uaData
        ?.getHighEntropyValues?.(['architecture'])
        .then((values) => {
          if (values.architecture === 'x86') setArch('x64');
          else if (values.architecture === 'arm') setArch('arm64');
        })
        .catch(() => {});
    } else if (detectedOs === 'windows') {
      setArch('x64');
    }
  }, []);

  return { os, arch };
}

interface Variant {
  href: string;
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  short: string;
}

function buildVariant(id: VariantId, t: (key: string) => string): Variant {
  switch (id) {
    case 'macArm64':
      return {
        href: DOWNLOADS.macArm64,
        icon: <AppleIcon />,
        label: t('downloadMac'),
        sublabel: t('downloadMacArm'),
        short: t('downloadShortMacArm'),
      };
    case 'win64':
      return {
        href: DOWNLOADS.win64,
        icon: <WindowsIcon />,
        label: t('downloadWindows'),
        sublabel: t('downloadWindowsArch'),
        short: t('downloadShortWin'),
      };
  }
}

function DownloadButton({ variant, primary = false }: { variant: Variant; primary?: boolean }) {
  return (
    <a
      href={variant.href}
      className={
        primary
          ? 'flex items-center gap-3 rounded-xl border border-info/40 bg-info/10 px-4 py-3.5 text-left transition hover:border-info/70 hover:bg-info/20'
          : 'flex items-center gap-3 rounded-xl border border-base-300 bg-base-100 px-4 py-3 text-left transition hover:border-info/50 hover:bg-info/10'
      }
    >
      <span
        className={
          primary
            ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-info/20 text-base-content'
            : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-base-200 text-base-content'
        }
      >
        {variant.icon}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-base-content">{variant.label}</span>
        <span className="block truncate text-xs text-base-content/60">{variant.sublabel}</span>
      </span>
    </a>
  );
}

function WindowsNote({ t }: { t: (key: string) => string }) {
  return (
    <p className="flex items-start gap-1.5 px-1 text-xs text-base-content/40">
      <svg
        viewBox="0 0 24 24"
        width={13}
        height={13}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        className="mt-0.5 shrink-0"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" strokeLinecap="round" />
      </svg>
      <span>{t('downloadWindowsNote')}</span>
    </p>
  );
}

/** OS 감지 전(또는 미지원 플랫폼) 폴백: 세 버튼을 동등하게 노출한다. */
function AllDownloads({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 md:grid-cols-1 lg:grid-cols-3">
        {VARIANT_ORDER.map((id) => (
          <DownloadButton key={id} variant={buildVariant(id, t)} />
        ))}
      </div>
      <WindowsNote t={t} />
    </div>
  );
}

/** OS 감지 후: 내 플랫폼에 맞는 주 버튼 + 나머지는 보조 링크. */
function SmartDownloads({ t, os }: { t: (key: string) => string; os: 'mac' | 'windows' }) {
  const primaryId: VariantId = os === 'mac' ? 'macArm64' : 'win64';
  const primary = buildVariant(primaryId, t);
  const others = VARIANT_ORDER.filter((id) => id !== primaryId).map((id) => buildVariant(id, t));

  return (
    <div className="flex flex-col gap-2.5">
      <DownloadButton variant={primary} primary />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
        <span className="text-xs text-base-content/40">{t('downloadOther')}</span>
        {others.map((variant) => (
          <a
            key={variant.href}
            href={variant.href}
            className="text-xs text-base-content/50 underline-offset-2 transition hover:text-base-content/80 hover:underline"
          >
            {variant.short}
          </a>
        ))}
      </div>
      {primaryId === 'win64' && <WindowsNote t={t} />}
    </div>
  );
}

export function DesktopDownloadSection() {
  const t = useLandingTranslations();
  const { os } = useDetectedPlatform();

  return (
    <Reveal>
      <section className="mt-8 border-y border-base-300/70 py-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-base-100 text-base-content">
              <DesktopIcon />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold">{t('downloadTitle')}</h3>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-base-content/60">{t('downloadSubtitle')}</p>
            </div>
          </div>
          <div className="w-full shrink-0 md:w-auto md:max-w-sm">
            {os === null ? <AllDownloads t={t} /> : <SmartDownloads t={t} os={os} />}
          </div>
        </div>
      </section>
    </Reveal>
  );
}
